import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import "../styles/showcase.css";

const tickerItems = [
  "Post-Quantum Secured",
  "Shielded EGOC Transfers",
  "P2P Compute Marketplace",
  "On-Chain Escrow",
  "App Hosting",
  "BFT Consensus",
  "AI Workspace",
  "Built on libp2p",
];

const modules = [
  "Compute",
  "Hosting",
  "Shielded Pay",
  "dApps",
  "AI Workspace",
  "Wallet",
];

const gpuRows = [
  { name: "RTX 4090 · 24 GB", price: "0.42 EGOC/hr" },
  { name: "A100 · 80 GB", price: "1.10 EGOC/hr" },
  { name: "Ryzen 9 · 32 cores", price: "0.08 EGOC/hr" },
];

const edge = [
  {
    n: "01",
    title: "Post-Quantum Security",
    text: "Dilithium signatures and Kyber key exchange alongside Ed25519 — keys and funds stay safe even against future quantum computers.",
  },
  {
    n: "02",
    title: "Self-Custody, No Accounts",
    text: "No sign-ups, billing accounts, or intermediaries. A recovery phrase is all you need — your keys, your EGOC, your data.",
  },
  {
    n: "03",
    title: "Proof, Not Trust",
    text: "Nodes earn EGOC for the storage and coverage they actually provide — verified on-chain by Proof of Coverage and Proof of Storage.",
  },
];

const spot = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
};

const rise = {
  hidden: { opacity: 0, y: 28 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 110, damping: 18 },
  },
};

function Ticker() {
  return (
    <div className="tick-strip" aria-hidden="true">
      <div className="tick-track">
        {[...tickerItems, ...tickerItems].map((t, i) => (
          <span className="tick-item" key={i}>
            <i />
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function Bento() {
  return (
    <section id="about" className="bento container">
      <motion.p
        className="bento-eyebrow"
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: false, amount: 0.6 }}
      >
        The Network
      </motion.p>
      <motion.h2
        className="bento-title"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        viewport={{ once: false, amount: 0.6 }}
      >
        One desktop.
        <br />
        <span className="grad-text">The whole network.</span>
      </motion.h2>
      <motion.p
        className="bento-lede"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        viewport={{ once: false, amount: 0.6 }}
      >
        Rent GPU and CPU power or earn EGOC by sharing your own. Host apps,
        send shielded transactions, build dApps — payments run on trustless
        on-chain escrow, no billing accounts, no intermediaries.
      </motion.p>

      <motion.div
        className="bento-grid"
        initial="hidden"
        whileInView="show"
        variants={{
          hidden: {},
          show: { transition: { staggerChildren: 0.09, delayChildren: 0.15 } },
        }}
        viewport={{ once: false, amount: 0.12 }}
      >
        <motion.div className="tile tile-win" variants={rise} onMouseMove={spot}>
          <div className="win-bar">
            <i />
            <i />
            <i />
            <span className="win-name">Ego Desktop</span>
            <span className="win-net">● Mainnet · Connected</span>
          </div>
          <div className="win-body">
            <div className="win-copy">
              <h3>Everything lives in Ego Desktop</h3>
              <p>
                One app to use the network and one app to power it — no
                juggling tools, no accounts.
              </p>
              <Link
                to="/download"
                className="win-cta"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                Download Ego Desktop
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </Link>
            </div>
            <div className="win-mods">
              {modules.map((m) => (
                <span className="mod-chip" key={m}>
                  <i />
                  {m}
                </span>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div className="tile tile-cmp" variants={rise} onMouseMove={spot}>
          <div className="tile-head">
            <h3>Compute Marketplace</h3>
            <span className="tile-tag">Rent or Earn</span>
          </div>
          <p className="tile-sub">
            A live market of GPUs and CPUs. List your hardware and earn EGOC
            per period while it&apos;s booked.
          </p>
          <div className="cmp-rows">
            {gpuRows.map((g, i) => (
              <div className="cmp-row" key={g.name} style={{ "--i": i }}>
                <span className="cmp-dot" />
                <span className="cmp-name">{g.name}</span>
                <span className="cmp-price">{g.price}</span>
                <span className="cmp-state" />
              </div>
            ))}
          </div>
          <div className="cmp-foot">
            <span className="pulse-dot" /> 3 providers online · settlement in
            EGOC
          </div>
        </motion.div>

        <motion.div className="tile tile-shd" variants={rise} onMouseMove={spot}>
          <div className="tile-head">
            <h3>Shielded Pay</h3>
          </div>
          <div className="shd-amt">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3l7 3v5c0 4.6-3 7.7-7 9-4-1.3-7-4.4-7-9V6l7-3Z" />
            </svg>
            <span className="shd-mask">
              <b>•</b>
              <b>•</b>
              <b>•</b>
              <b>•</b>
              <b>•</b>
            </span>
            <span className="shd-unit">EGOC</span>
          </div>
          <p className="tile-sub">
            Amounts and balances stay confidential on-chain — visible to sender
            and receiver only.
          </p>
        </motion.div>

        <motion.div className="tile tile-hst" variants={rise} onMouseMove={spot}>
          <div className="tile-head">
            <h3>App Hosting</h3>
          </div>
          <div className="hst-term">
            <span className="hst-cmd">$ ego deploy ./app</span>
            <span className="hst-bar">
              <span className="hst-fill" />
            </span>
            <span className="hst-ok">● live on 3 nodes</span>
          </div>
          <p className="tile-sub">
            Deploy apps and services to provider capacity, settled on-chain.
          </p>
        </motion.div>

        <motion.div className="tile tile-dap" variants={rise} onMouseMove={spot}>
          <div className="tile-head">
            <h3>Build dApps</h3>
          </div>
          <div className="dap-bars">
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
          <p className="tile-sub">
            Ship on Ego with EgoScan indexing every block as you build.
          </p>
        </motion.div>

        <motion.div className="tile tile-ai" variants={rise} onMouseMove={spot}>
          <div className="tile-head">
            <h3>AI Workspace</h3>
          </div>
          <div className="ai-line">
            <span className="ai-prompt">&gt;</span> run llama-70b on rented
            A100s<span className="ai-caret" />
          </div>
          <p className="tile-sub">
            LLMs, notebooks and image generation on marketplace compute.
          </p>
        </motion.div>

        <motion.div className="tile tile-esc" variants={rise} onMouseMove={spot}>
          <div className="esc-copy">
            <h3>On-Chain Escrow</h3>
            <p className="tile-sub">
              Funds lock at booking and stream out on every provider heartbeat.
              Missed beats auto-refund the rest.
            </p>
          </div>
          <svg className="esc-ekg" viewBox="0 0 320 60" preserveAspectRatio="none" aria-hidden="true">
            <polyline
              className="esc-line"
              points="0,30 60,30 76,30 86,10 96,50 106,30 180,30 196,30 206,12 216,48 226,30 320,30"
              fill="none"
            />
          </svg>
          <div className="esc-steps">
            <span>lock</span>
            <em>→</em>
            <span>heartbeat</span>
            <em>→</em>
            <span className="on">release</span>
          </div>
        </motion.div>
      </motion.div>

      <div className="bento-more">
        <Link
          to="/docs"
          className="docs-link"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          Learn more in the docs →
        </Link>
      </div>
    </section>
  );
}

function Edge() {
  return (
    <section id="features" className="edge container">
      <motion.p
        className="bento-eyebrow"
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: false, amount: 0.6 }}
      >
        What Sets Ego Apart
      </motion.p>
      <div className="edge-rows">
        {edge.map((f, i) => (
          <motion.div
            className="edge-row"
            key={f.n}
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08, type: "spring", stiffness: 110, damping: 18 }}
            viewport={{ once: false, amount: 0.4 }}
          >
            <span className="edge-n">{f.n}</span>
            <h3 className="edge-title">{f.title}</h3>
            <p className="edge-text">{f.text}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

export default function HomeShowcase() {
  return (
    <>
      <Ticker />
      <Bento />
      <Edge />
    </>
  );
}
